import { motion } from 'framer-motion'
import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { pageUrl } from '../seo'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

export default function Direitos() {
    const { t } = useTranslation()

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            {/* SEO por rota — no React 19 estas tags são içadas para o <head> */}
            <title>{t('direitos.metaTitle')}</title>
            <meta name="description" content={t('direitos.metaDescription')} />
            <link rel="canonical" href={pageUrl('direitos')} />
            {/* Hero Section */}
            <section
                className="relative overflow-hidden py-16 lg:py-20 bg-cover bg-center"
                style={{ backgroundImage: "url('/assets/images/hero-bg.jpg')" }}
            >
                <div className="absolute inset-0 bg-black/55" />
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            {t('direitos.title')}
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Intro */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <div className="max-w-4xl space-y-4 text-slate-700 leading-relaxed text-lg">
                            <p>{t('direitos.intro.p1')}</p>
                            <p>{t('direitos.intro.p2')}</p>
                            <p>{t('direitos.intro.p3')}</p>
                        </div>
                        <div className="mt-6">
                            <a
                                href="https://fra.europa.eu/pt/about-fundamental-rights"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-brand-600 font-bold hover:underline"
                            >
                                {t('direitos.intro.fraLink')} <ExternalLink size={14} aria-hidden="true" />
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Direitos de pessoas estrangeiras vítimas de crime */}
            <section className="section-padding bg-slate-50">
                <div className="container-custom">
                    <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6">
                            {t('direitos.vitimas.title')}
                        </h2>
                        <div className="max-w-4xl space-y-4 text-slate-700 leading-relaxed">
                            <p>{t('direitos.vitimas.p1')}</p>
                            <p>{t('direitos.vitimas.p2')}</p>
                            <p>{t('direitos.vitimas.p3')}</p>
                            <p>{t('direitos.vitimas.p4')}</p>
                        </div>
                        <div className="mt-4">
                            <a
                                href="https://www.infovitimas.pt/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-brand-600 font-bold hover:underline"
                            >
                                {t('direitos.vitimas.link')} <ExternalLink size={14} aria-hidden="true" />
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Outros direitos de pessoas migrantes */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }}>
                            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-8">
                                {t('direitos.outros.title')}
                            </h2>

                            {/* Entrada e permanência */}
                            <div className="mb-8 bg-slate-50 rounded-2xl p-7 border border-slate-100">
                                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                                    <span className="text-brand-600" aria-hidden="true">&gt;</span> {t('direitos.outros.entrada.title')}
                                </h3>
                                <div className="space-y-3 text-slate-700 leading-relaxed mb-4">
                                    <p>{t('direitos.outros.entrada.p1')}</p>
                                    <p>{t('direitos.outros.entrada.p2')}</p>
                                </div>
                                <a
                                    href="https://aima.gov.pt/pt"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-brand-600 font-bold hover:underline"
                                >
                                    {t('direitos.outros.entrada.link')} <ExternalLink size={14} aria-hidden="true" />
                                </a>
                            </div>

                            {/* Direito ao trabalho */}
                            <div className="bg-slate-50 rounded-2xl p-7 border border-slate-100">
                                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                                    <span className="text-brand-600" aria-hidden="true">&gt;</span> {t('direitos.outros.trabalho.title')}
                                </h3>
                                <p className="text-slate-700 leading-relaxed">
                                    {t('direitos.outros.trabalho.text')}
                                </p>
                            </div>
                        </motion.div>

                        {/* Right: InfoVítimas poster — clickable link to PDF */}
                        <motion.a
                            href="https://apav.org.pt/publiproj/images/yootheme/PDF/infovitimas_Cartaz_PT.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="block hover:opacity-90 transition-opacity shadow-xl rounded-xl overflow-hidden max-w-sm w-full mx-auto"
                        >
                            <img
                                src="/assets/images/infovitimas-cartaz.png"
                                alt={t('direitos.posterAlt')}
                                className="w-full h-auto"
                            />
                        </motion.a>
                    </div>
                </div>
            </section>
        </motion.div>
    )
}
